import React from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  leftAddon?: React.ReactNode;
  rightAddon?: React.ReactNode;
}

export const Input: React.FC<InputProps> = ({
  label,
  hint,
  error,
  leftAddon,
  rightAddon,
  id,
  className = '',
  ...rest
}) => {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && (
        <label htmlFor={inputId} className="text-[13px] font-semibold text-v2-neutral-700">
          {label}
          {rest.required && <span className="text-v2-red-400 ml-0.5">*</span>}
        </label>
      )}
      <div className="relative flex items-center">
        {leftAddon && (
          <span className="absolute left-4 text-v2-neutral-400">{leftAddon}</span>
        )}
        <input
          id={inputId}
          className={`
            w-full px-4 py-[13px] border-[1.5px] rounded-v2-md font-sans text-[15px]
            text-v2-neutral-800 bg-v2-neutral-0 outline-none transition-colors duration-150
            placeholder:text-v2-neutral-300
            ${error ? 'border-v2-red-400 focus:border-v2-red-400' : 'border-v2-neutral-200 focus:border-v2-green-400'}
            ${leftAddon ? 'pl-10' : ''}
            ${rightAddon ? 'pr-10' : ''}
            ${className}
          `}
          {...rest}
        />
        {rightAddon && (
          <span className="absolute right-4 text-v2-neutral-400">{rightAddon}</span>
        )}
      </div>
      {error && <p className="text-[12px] text-v2-red-400">{error}</p>}
      {hint && !error && <p className="text-[12px] text-v2-neutral-400">{hint}</p>}
    </div>
  );
};

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Textarea: React.FC<TextareaProps> = ({
  label,
  hint,
  error,
  id,
  className = '',
  ...rest
}) => {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && (
        <label htmlFor={inputId} className="text-[13px] font-semibold text-v2-neutral-700">
          {label}
          {rest.required && <span className="text-v2-red-400 ml-0.5">*</span>}
        </label>
      )}
      <textarea
        id={inputId}
        className={`
          w-full px-4 py-[13px] border-[1.5px] rounded-v2-md font-sans text-[15px]
          text-v2-neutral-800 bg-v2-neutral-0 outline-none transition-colors duration-150
          placeholder:text-v2-neutral-300 resize-none
          ${error ? 'border-v2-red-400 focus:border-v2-red-400' : 'border-v2-neutral-200 focus:border-v2-green-400'}
          ${className}
        `}
        {...rest}
      />
      {error && <p className="text-[12px] text-v2-red-400">{error}</p>}
      {hint && !error && <p className="text-[12px] text-v2-neutral-400">{hint}</p>}
    </div>
  );
};

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  error?: string;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export const Select: React.FC<SelectProps> = ({
  label,
  hint,
  error,
  options,
  placeholder,
  id,
  className = '',
  ...rest
}) => {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && (
        <label htmlFor={inputId} className="text-[13px] font-semibold text-v2-neutral-700">
          {label}
          {rest.required && <span className="text-v2-red-400 ml-0.5">*</span>}
        </label>
      )}
      <select
        id={inputId}
        className={`
          w-full px-4 py-[13px] border-[1.5px] rounded-v2-md font-sans text-[15px]
          text-v2-neutral-800 bg-v2-neutral-0 outline-none transition-colors duration-150
          appearance-none cursor-pointer
          ${error ? 'border-v2-red-400' : 'border-v2-neutral-200 focus:border-v2-green-400'}
          ${className}
        `}
        {...rest}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {error && <p className="text-[12px] text-v2-red-400">{error}</p>}
      {hint && !error && <p className="text-[12px] text-v2-neutral-400">{hint}</p>}
    </div>
  );
};
